import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';

const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5">
    <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="0" y="1" width="14" height="6" rx="1" fill="#f43f5e" />
      <rect x="16" y="1" width="16" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="9" width="9" height="6" rx="1" fill="#e11d48" />
      <rect x="11" y="9" width="11" height="6" rx="1" fill="#f43f5e" />
      <rect x="24" y="9" width="8" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="17" width="15" height="6" rx="1" fill="#f43f5e" />
      <rect x="17" y="17" width="15" height="6" rx="1" fill="#e11d48" />
      <rect x="0" y="25" width="10" height="6" rx="1" fill="#e11d48" />
      <rect x="12" y="25" width="12" height="6" rx="1" fill="#f43f5e" />
      <rect x="26" y="25" width="6" height="6" rx="1" fill="#e11d48" />
    </svg>
    <span className="text-lg font-semibold tracking-tight text-white">
      saleswall
    </span>
  </Link>
);

interface LocationState {
  from?: { pathname?: string };
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as LocationState | null)?.from?.pathname || '/';

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      const dashboardRoutes: Record<string, string> = {
        vendor: '/vendor',
        merchant: '/merchant',
        admin: '/admin',
      };
      navigate(dashboardRoutes[user.role] || from, { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex flex-col">
      <div className="p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-rose-400 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm">Back to home</span>
        </Link>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="w-full max-w-md">
          <div className="flex justify-center mb-8">
            <Logo />
          </div>

          <div className="bg-[#18181b] rounded-2xl border border-white/10 p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold text-white mb-2">Welcome back</h1>
              <p className="text-zinc-400 text-sm">Sign in to your account to continue</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {error && (
                <div className="p-3 rounded-lg bg-rose-500/10 border border-rose-500/20 text-rose-400 text-sm" data-testid="login-error">
                  {error}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-zinc-300">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-11 bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-rose-500/50 focus:ring-rose-500/20"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-zinc-300">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="login-password-input"
                    className="h-11 pr-10 bg-white/5 border-white/10 text-white placeholder:text-zinc-500 focus:border-rose-500/50 focus:ring-rose-500/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full h-11 bg-rose-500 hover:bg-rose-600 text-white font-medium"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign in'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-zinc-400">
                Don't have an account?{' '}
                <Link to="/register" className="text-rose-400 hover:text-rose-300 font-medium">
                  Sign up
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
